import React, { useEffect, useMemo, useState } from "react";
import { switchMap, catchError, tap, map, of } from "rxjs";
import PropTypes from "prop-types";
import withErrorGuard from "../../HOC/withErrorGuard";
import Pulsar from "../Pulsar";
import getTracks from "./getExomeDepthCnvTracks";
import getApiClient from "../Pulsar/apiClient";
import {
  READ_RATIO_CL,
  COVERAGE_CL,
  CALL_CL,
  TRANSCRIPT_CL,
} from "../Pulsar/constants/options";
import InfoRenderer from "./InfoRenderer";
import WebglErrorView from "../../HOC/withErrorGuard/WebglErrorView";
import defaultErrorHandler from "../Pulsar/helpers/defaultErrorHandler";
import parseQcReport from "./parseQcReport";

function ExomeDepthBrowserInternal({
  start,
  end,
  visStart,
  visEnd,
  analysisId,
  originAnalId,
  genome,
  sampleId,
  rootAnalysis,
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [qcReport, setQcReport] = useState({});
  const tracks = useMemo(() => getTracks(sampleId), [sampleId]);

  useEffect(() => {
    const { getRefSamples } = getApiClient();
    const sub = of(null)
      .pipe(
        switchMap(() => getRefSamples({ originAnalId })),
        map((x) => parseQcReport(x)),
        tap((x) => setQcReport(x)),
        catchError(defaultErrorHandler),
        tap(() => setIsLoading(false))
      )
      .subscribe();

    return () => sub.unsubscribe();
  }, [analysisId]);

  return isLoading ? (
    <div style={{ minHeight: "calc(100vh - 200px)" }}>
      <div className="tw-italic tw-flex tw-p-4 tw-justify-center tw-bg-blueGray-200 tw-border tw-border-blueGray-300">
        <div className="tw-inline-block tw-mx-4">Loading...</div>
        <div className="tw-inline-block">
          <i className="fa fa-circle-o-notch fa-spin" />
        </div>
      </div>
    </div>
  ) : (
    <Pulsar
      title="CNV Browser"
      key="cnv-browser"
      trackConfs={tracks}
      visStart={visStart}
      visEnd={visEnd}
      start={start}
      end={end}
      allowedColorScales={[TRANSCRIPT_CL, READ_RATIO_CL, COVERAGE_CL, CALL_CL]}
      state={{
        rootAnalysis,
        sampleCsv: `graphsample.${analysisId}.csv`,
        originAnalId,
        genome,
        qcReport,
        sampleId,
        previewId: null,
        graphSampleDetails: {},
      }}
      InfoRenderer={InfoRenderer}
    />
  );
}

ExomeDepthBrowserInternal.propTypes = {
  start: PropTypes.number.isRequired,
  end: PropTypes.number.isRequired,
  visStart: PropTypes.number.isRequired,
  visEnd: PropTypes.number.isRequired,
  analysisId: PropTypes.string.isRequired,
  originAnalId: PropTypes.string.isRequired,
  genome: PropTypes.string.isRequired,
  rootAnalysis: PropTypes.string.isRequired,
  sampleId: PropTypes.string.isRequired,
};

ExomeDepthBrowserInternal.defaultProps = {};

export default withErrorGuard(
  ExomeDepthBrowserInternal,
  "CnvBrowser",
  "Cnv Browser",
  WebglErrorView
);
